import { Button, Stack, Text } from "flicket-ui";
import { SocialShareFooter } from "~components/common/Social/SocialShareFooter";
import CustomModal from "~components/CustomModal";
import { CompetitionProvider } from "~features/competitions/CompetitionContext";
import { createPromoterTitles } from "~features/competitions/helpers";
import { CompetitionType } from "~graphql/typed-document-nodes";
import { useOrganization } from "~hooks";
import { useActiveCompetitionByType } from "../hooks/useActiveCompetitions";
import { PageContext } from "../hooks/useDetailsPageContext";
import { PromoterShareCard } from "./PromoterShareCard";
import AnimatedLoadingSpinner from "~features/single-page-checkout/components/AnimatedLoadingSpinner/AnimatedLoadingSpinner";
import { useRouter } from "next/router";
import { useState } from "react";
import { useTimeout } from "usehooks-ts";

interface RegistrationSuccessModalContentProps {
  pageContext: PageContext;
}

export const RegistrationSuccessModalContent = ({
  pageContext,
}: RegistrationSuccessModalContentProps) => {
  const {
    event: { presaleRelease },
  } = pageContext;

  const { hasFeature } = useOrganization();

  const isPrePurchaseActive =
    presaleRelease?.isPrePurchaseActive &&
    hasFeature("prePurchaseRegistration");

  return isPrePurchaseActive ? (
    <RegistrationSuccessWithPrePurchaseCard pageContext={pageContext} />
  ) : (
    <RegistrationSuccessWithShareCard pageContext={pageContext} />
  );
};

const RegistrationSuccessWithShareCard = ({
  pageContext,
}: RegistrationSuccessModalContentProps) => {
  const {
    waitlistUserId,
    event: { id: eventId, title: eventTitle },
    userEmail,
  } = pageContext;

  const { activeCompetition } = useActiveCompetitionByType(
    CompetitionType.Registration,
    eventId
  );

  const { title, subtitle } = createPromoterTitles(
    waitlistUserId && activeCompetition ? activeCompetition : undefined
  );

  return (
    <CompetitionProvider
      userId={waitlistUserId}
      competition={activeCompetition}
    >
      <CustomModal.Header>You&lsquo;re on the list 🎉</CustomModal.Header>
      <CustomModal.Content>
        <Stack direction={"vertical"} gap={4}>
          <Text variant="regular">
            We&lsquo;ll send an email to {userEmail} as soon as presale tickets
            are available.
          </Text>
          <Stack direction={"vertical"}>
            <Text variant="header.S">{title}</Text>
            {subtitle && <Text variant="regular">{subtitle}</Text>}
          </Stack>
          <PromoterShareCard
            eventId={eventId}
            eventTitle={eventTitle}
            waitlistUserId={waitlistUserId}
            eventPhase={CompetitionType.Registration}
          />
        </Stack>
      </CustomModal.Content>
      {!!activeCompetition && <SocialShareFooter />}
    </CompetitionProvider>
  );
};

const FINDING_BEST_PRICE_DURATION = 500;

function FindingTheBestPriceAnimation({
  onComplete,
}: {
  onComplete: () => void;
}) {
  const [loading, setLoading] = useState(true);

  const onAnimationComplete = () => {
    setTimeout(onComplete, 200);
  };

  useTimeout(() => {
    setLoading(false);
  }, FINDING_BEST_PRICE_DURATION);

  return (
    <>
      <CustomModal.Content>
        <Stack
          direction="vertical"
          gap={1}
          alignItems={"center"}
          justifyContent={"center"}
          height="386px"
        >
          <Text variant="header.M" textAlign={{ _: "center", md: "start" }}>
            Hold tight – we’re finding you the best deal.
          </Text>
          <AnimatedLoadingSpinner
            loading={loading}
            onComplete={onAnimationComplete}
          />
        </Stack>
      </CustomModal.Content>
    </>
  );
}

const RegistrationSuccessWithPrePurchaseCard = ({
  pageContext,
}: RegistrationSuccessModalContentProps) => {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const presaleLink = `/events/${pageContext.event.id}/reservation?release=${pageContext.event.presaleRelease?.id}`;

  if (loading) {
    return (
      <FindingTheBestPriceAnimation
        onComplete={() => {
          void router.push(presaleLink);
        }}
      />
    );
  }

  return (
    <>
      <CustomModal.Header>You&lsquo;re all set 🎉</CustomModal.Header>
      <CustomModal.Content>
        <Stack direction={"vertical"} gap={4}>
          <Text variant="regular" textAlign={{ _: "center", md: "start" }}>
            We&lsquo;ll send an email to {pageContext.userEmail} as soon as
            presale tickets are available.
          </Text>
          <Stack
            direction="vertical"
            backgroundColor="darken3"
            borderRadius="md"
            padding={4}
            gap={3}
            textAlign="center"
          >
            <Text variant="header.M">
              Beat the crowd. Secure your tickets early.
            </Text>
            <Stack direction="vertical" gap={2}>
              <Button variant="primary" onClick={() => setLoading(true)}>
                Secure best price tickets
              </Button>
              <Text variant="regular">
                Secure early access at today&apos;s price. You&apos;ll be
                charged when the presale opens, and can cancel anytime
                beforehand.
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </CustomModal.Content>
    </>
  );
};
